import { alpha } from '@mui/material/styles';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
} from 'react';
import { Box } from '@mui/material';
import {
  createRangeAtEnd,
  domToSegments,
  getRangeInsideEditor,
  insertPlainTextAtRange,
  insertTagAtRange,
  renderSegmentsIntoElement,
} from './utils/transcriptInlineChipDom';
import {
  normalizeSegments,
  segmentsEqual,
} from './utils/transcriptInlineChipUtils';

/**
 * Transcript area with inline tag chips at caret; plain text includes ` [TagName] ` for API / linked fields.
 */
const TranscriptInlineChipEditor = forwardRef(
  function TranscriptInlineChipEditor(
    {
      id,
      segments,
      onSegmentsChange,
      readOnly = false,
      minRows = 3,
      placeholder = '',
      dir = 'ltr',
      'aria-label': ariaLabel,
      sx,
      onClick,
      /** Called after DOM is synced on blur (e.g. recompute transcript change %). */
      onCommit,
    },
    ref,
  ) {
    const rootRef = useRef(null);
    const isFocusedRef = useRef(false);
    /** Last selection inside the editor (survives blur to tag picker / paste). */
    const lastCaretRangeRef = useRef(null);
    const segmentsRef = useRef(segments);
    segmentsRef.current = segments;

    const pickInsertRange = useCallback(() => {
      const el = rootRef.current;
      if (!el) return null;
      const live = getRangeInsideEditor(el);
      if (live) return live;
      if (lastCaretRangeRef.current) {
        try {
          const r = lastCaretRangeRef.current.cloneRange();
          if (el.contains(r.commonAncestorContainer)) return r;
        } catch {
          /* detached / invalid */
        }
      }
      return createRangeAtEnd(el);
    }, []);

    useEffect(() => {
      let rafId = null;
      const onSelectionChange = () => {
        if (rafId != null) return;
        rafId = globalThis.requestAnimationFrame(() => {
          rafId = null;
          try {
            const root = rootRef.current;
            if (!root || readOnly) return;
            const sel = globalThis.getSelection?.();
            if (!sel?.rangeCount) return;
            const r = sel.getRangeAt(0);
            if (!root.contains(r.commonAncestorContainer)) return;
            lastCaretRangeRef.current = r.cloneRange();
          } catch {
            /* DOM may be torn down; ignore. */
          }
        });
      };
      document.addEventListener('selectionchange', onSelectionChange);
      return () => {
        document.removeEventListener('selectionchange', onSelectionChange);
        if (rafId != null) globalThis.cancelAnimationFrame(rafId);
      };
    }, [readOnly]);

    const emitFromDom = useCallback(() => {
      const el = rootRef.current;
      if (!el) return;
      const next = domToSegments(el);
      onSegmentsChange?.(next);
    }, [onSegmentsChange]);

    useLayoutEffect(() => {
      const el = rootRef.current;
      if (!el) return;
      if (isFocusedRef.current) return;
      const domSegs = domToSegments(el);
      if (segmentsEqual(domSegs, segments)) return;
      renderSegmentsIntoElement(el, segments, readOnly);
    }, [segments, readOnly]);

    useImperativeHandle(
      ref,
      () => ({
        focus: () => rootRef.current?.focus(),
        insertTag: (name) => {
          const el = rootRef.current;
          if (!el || readOnly) return;
          const range = pickInsertRange();
          if (!range) return;
          const r = range.cloneRange();
          el.focus();
          insertTagAtRange(el, name, r);
          emitFromDom();
        },
        insertPlainTextAtCaret: (text) => {
          const el = rootRef.current;
          if (!el || readOnly) return;
          const range = pickInsertRange();
          if (!range) return;
          const r = range.cloneRange();
          el.focus();
          insertPlainTextAtRange(el, text, r);
          emitFromDom();
        },
        /** Replace entire document (e.g. external linked field sync). */
        replaceAllPlainText: (text) => {
          const next = normalizeSegments([
            { type: 'text', text: String(text ?? '') },
          ]);
          onSegmentsChange?.(next);
        },
      }),
      [readOnly, emitFromDom, onSegmentsChange, pickInsertRange],
    );

    const handleInput = useCallback(() => {
      emitFromDom();
    }, [emitFromDom]);

    const handleChipRemove = useCallback(
      (e) => {
        if (readOnly) return;
        const btn = e.target.closest?.('.qc-inline-chip-remove');
        if (!btn) return;
        e.preventDefault();
        e.stopPropagation();
        const chip = btn.closest('.qc-inline-chip');
        chip?.remove();
        emitFromDom();
      },
      [readOnly, emitFromDom],
    );

    const handlePaste = useCallback(
      (e) => {
        if (readOnly) return;
        e.preventDefault();
        const t = e.clipboardData?.getData('text/plain') ?? '';
        if (t === '') return;
        const range = pickInsertRange();
        if (range) {
          insertPlainTextAtRange(rootRef.current, t, range.cloneRange());
        }
        emitFromDom();
      },
      [readOnly, emitFromDom, pickInsertRange],
    );

    /** Mount: ensure DOM matches segments. */
    useEffect(() => {
      const el = rootRef.current;
      if (!el) return;
      if (el.childNodes.length > 0) return;
      renderSegmentsIntoElement(el, segmentsRef.current, readOnly);
    }, [readOnly]);

    return (
      <Box
        sx={[
          (theme) => ({
            position: 'relative',
            width: '100%',
            minHeight: theme.spacing(minRows * 3),
            borderRadius: '8px',
            border: '1px solid',
            borderColor: readOnly
              ? theme.palette.divider
              : theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.23)'
                : 'rgba(0,0,0,0.23)',
            bgcolor: readOnly
              ? theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.05)'
                : 'rgba(0,0,0,0.02)'
              : theme.palette.background.paper,
            transition: theme.transitions.create(['border-color'], {
              duration: theme.transitions.duration.shorter,
            }),
            '&:focus-within': readOnly
              ? {}
              : {
                  borderColor: theme.palette.primary.main,
                  borderWidth: 2,
                  boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.25)}`,
                },
          }),
          ...(Array.isArray(sx) ? sx : sx != null ? [sx] : []),
        ]}
      >
        <Box
          id={id}
          ref={rootRef}
          component="div"
          dir={dir}
          role="textbox"
          aria-multiline="true"
          aria-label={ariaLabel ?? 'Updated transcript'}
          contentEditable={!readOnly}
          suppressContentEditableWarning
          onInput={handleInput}
          onCompositionEnd={handleInput}
          onClick={(e) => {
            handleChipRemove(e);
            onClick?.(e);
          }}
          onPaste={handlePaste}
          onFocus={() => {
            isFocusedRef.current = true;
          }}
          onBlur={(e) => {
            isFocusedRef.current = false;
            try {
              const el = rootRef.current;
              const sel = globalThis.getSelection?.();
              if (
                el &&
                sel?.rangeCount &&
                el.contains(sel.getRangeAt(0).commonAncestorContainer)
              ) {
                lastCaretRangeRef.current = sel.getRangeAt(0).cloneRange();
              }
            } catch {
              /* ignore */
            }
            emitFromDom();
            onCommit?.();
          }}
          sx={(theme) => ({
            minHeight: theme.spacing(minRows * 3),
            maxHeight: { xs: theme.spacing(22), sm: theme.spacing(24) },
            overflowY: 'auto',
            px: 1.25,
            py: 0.75,
            outline: 'none',
            fontSize: { xs: '0.8125rem', sm: '0.8125rem', md: '0.8125rem' },
            lineHeight: 1.43,
            color: 'text.primary',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            '&:empty:before': placeholder
              ? {
                  content: `"${placeholder.replace(/"/g, '\\"')}"`,
                  color: alpha(theme.palette.text.primary, 0.38),
                  pointerEvents: 'none',
                }
              : {},
            '& .qc-inline-chip': {
              display: 'inline-flex',
              alignItems: 'center',
              verticalAlign: 'middle',
              gap: 0.25,
              maxWidth: '100%',
              margin: '0 2px',
              padding: '2px 4px 2px 8px',
              borderRadius: theme.spacing(1),
              fontSize: '0.75rem',
              fontWeight: 700,
              lineHeight: 1.2,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
              userSelect: 'none',
              WebkitUserSelect: 'none',
            },
            '& .qc-inline-chip-label': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: {
                xs: 'min(60vw, 200px)',
                sm: 240,
                md: 260,
                lg: 280,
                xl: 300,
              },
            },
            '& .qc-inline-chip-remove': {
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: 0,
              padding: '2px',
              minWidth: 22,
              minHeight: 22,
              border: 'none',
              borderRadius: '6px',
              background: 'transparent',
              color: 'inherit',
              cursor: readOnly ? 'default' : 'pointer',
              fontSize: '1rem',
              lineHeight: 1,
              '&:hover': readOnly
                ? {}
                : {
                    bgcolor: alpha(theme.palette.error.main, 0.12),
                    color: theme.palette.error.main,
                  },
            },
          })}
        />
      </Box>
    );
  },
);

export default TranscriptInlineChipEditor;
